import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Globe, Server, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../api";

const PageShell = ({ eyebrow, title, children, testid }) => (
  <div className="mx-auto max-w-3xl px-6 py-16 md:px-8 lg:py-24" data-testid={testid}>
    <Reveal>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sage">{eyebrow}</p>
      <h1 className="mt-3 font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl">{title}</h1>
      <div className="mt-8 space-y-6 text-[15px] font-light leading-relaxed text-ink/70">{children}</div>
    </Reveal>
  </div>
);

export const About = () => (
  <div data-testid="about-page">
    <PageShell eyebrow="Our story" title="Made on paper, first">
      <img src={IMAGES.workspace} alt="The Bloom Paper Studio desk" className="rounded-[1.6rem] border border-sage/10 object-cover" />
      <p>
        Bloom Paper Studio began at a kitchen table covered in grid paper, washi tape and too many
        fine-liner pens. Every planner, tracker and template in the shop starts life as a hand-drawn
        spread — tested in real study sessions, real budgets and real habits — before it is pressed
        into a digital file.
      </p>
      <p>
        The philosophy is simple: a planning tool should calm you, not nag you. Soft palettes,
        generous white space and structures that bend around your life instead of the other way
        around. Everything here is an original work, designed slowly and released when it's ready.
      </p>
      <p>
        Because every product is a digital download, there's no packaging, no shipping and no
        waiting — just a file that's yours the moment you check out, to print at home or use on a
        tablet, again and again.
      </p>
    </PageShell>
  </div>
);

const FAQS = [
  {
    q: "How do I receive my files?",
    a: "The moment your payment is confirmed by Stripe, you're taken to a download page and a receipt with your personal download link is emailed to you. No account is needed — just keep that email safe.",
  },
  {
    q: "What file formats do I get?",
    a: "Each product page lists its exact formats. Most planners include print-ready PDFs (A4 and US Letter) and hyperlinked versions for GoodNotes and other annotation apps; template packs include editable source files.",
  },
  {
    q: "Can I get a refund?",
    a: "Because digital files can't be returned, all sales are final once downloaded. If a file is faulty or you were charged in error, contact the studio within 14 days and it will be put right.",
  },
  {
    q: "What can I do with my purchase?",
    a: "Personal-licence items are yours to print and use as often as you like, but the files can't be shared, resold or redistributed. Template packs include a small commercial licence — each product page states its exact terms.",
  },
  {
    q: "Do you accept international payments?",
    a: "Yes. Checkout is powered by Stripe, which accepts major cards, Apple Pay and Google Pay from most countries, with currency conversion handled automatically.",
  },
  {
    q: "Is there a download limit?",
    a: "Each purchase includes up to 25 downloads of your files — plenty for new devices and fresh prints. If you ever run out, just contact the studio.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <PageShell eyebrow="Good to know" title="Frequently asked questions" testid="faq-page">
      <div className="space-y-3">
        {FAQS.map((f, i) => (
          <div key={i} className="overflow-hidden rounded-2xl border border-sage/12 bg-white/70" data-testid={`faq-item-${i}`}>
            <button
              data-testid={`faq-question-${i}`}
              onClick={() => setOpen(open === i ? -1 : i)}
              className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-serif text-lg text-ink transition-colors hover:text-sage"
            >
              {f.q}
              <ChevronDown size={18} className={`shrink-0 text-sage transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <p className="px-6 pb-6 text-sm leading-relaxed text-ink/60">{f.a}</p>}
          </div>
        ))}
      </div>
    </PageShell>
  );
};

export const Privacy = () => (
  <PageShell eyebrow="Fine print" title="Privacy policy" testid="privacy-page">
    <p>
      Bloom Paper Studio collects only what's needed to deliver your order: your email address and
      purchase details. Payment information is processed directly by Stripe and never touches our
      servers.
    </p>
    <p>
      Your email is used to send your receipt and download link, and to reply if you write to us.
      It is never sold, rented or used for marketing without your consent.
    </p>
    <p>
      The owner dashboard is protected by encrypted, owner-only sign-in. Product files are stored
      privately and are only ever served through verified, per-order download links.
    </p>
    <p>
      You may ask for your order data to be corrected or deleted at any time via the contact page.
      This policy was last updated in July 2026.
    </p>
  </PageShell>
);

export const Policy = () => (
  <PageShell eyebrow="Fine print" title="Shop policies" testid="policy-page">
    <h2 className="font-serif text-2xl text-ink">Digital delivery</h2>
    <p>
      All products are digital downloads. Delivery is automatic and immediate once Stripe confirms
      your payment — you'll see a download page and receive a receipt email with your personal link.
    </p>
    <h2 className="font-serif text-2xl text-ink">Refunds</h2>
    <p>
      Digital files cannot be returned, so all sales are final once the files have been downloaded.
      Faulty files or duplicate charges will always be corrected or refunded within 14 days — just
      get in touch.
    </p>
    <h2 className="font-serif text-2xl text-ink">Licences</h2>
    <p>
      Unless a product page says otherwise, purchases carry a personal licence: unlimited personal
      printing and digital use, but no sharing, resale or redistribution of the files. Editable
      template packs carry a small commercial licence as described on their pages.
    </p>
    <h2 className="font-serif text-2xl text-ink">Fair use of download links</h2>
    <p>
      Each order includes up to 25 downloads. Links are personal to the buyer; links that are shared
      publicly may be retired.
    </p>
  </PageShell>
);

const DOMAIN_STEPS = [
  {
    icon: Globe,
    title: "1 · Own a domain",
    text: "Buy your domain from any registrar (Namecheap, Cloudflare, GoDaddy…). Something like bloompaperstudio.com.",
  },
  {
    icon: Server,
    title: "2 · Deploy this site",
    text: "In your Emergent workspace, press Deploy to publish the app. Then open Manage → Publishing → Domains and add your custom domain.",
  },
  {
    icon: ArrowDnsIcon,
    title: "3 · Point your DNS",
    text: "At your registrar, create the records shown in the Domains panel — typically an A record for the root domain (@) pointing to the provided IP, and a CNAME record for www pointing to the provided host. Delete any conflicting parked records.",
  },
  {
    icon: ShieldCheck,
    title: "4 · Wait for SSL",
    text: "Once DNS resolves, the SSL certificate is provisioned automatically and your shop opens on your own domain over https. DNS changes can take up to 24–48 hours, though it's usually minutes.",
  },
];

function ArrowDnsIcon(props) {
  return <CheckCircle2 {...props} />;
}

export const DomainSetup = () => (
  <div className="mx-auto max-w-3xl px-6 py-16 md:px-8 lg:py-24" data-testid="domain-setup-container">
    <Reveal>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sage">Going live</p>
      <h1 className="mt-3 font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl">
        Connect your own domain
      </h1>
      <p className="mt-5 text-base font-light leading-relaxed text-ink/65">
        Your shop runs on a preview address while you build. When you're ready, give it a permanent
        home on your own domain in four steps:
      </p>
      <div className="mt-10 space-y-4" data-testid="domain-dns-instructions">
        {DOMAIN_STEPS.map((s, i) => (
          <div key={i} className="flex gap-5 rounded-2xl border border-sage/12 bg-white/70 p-6">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-lavender-tint text-lavender-deep">
              <s.icon size={19} />
            </span>
            <div>
              <h3 className="font-serif text-xl text-ink">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink/60">{s.text}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-8 rounded-2xl bg-sage-tint/60 p-6 text-sm leading-relaxed text-sage-deep">
        Tip: payments and receipts work on any domain automatically — after connecting yours, your
        existing Stripe checkout and download emails keep working with no extra setup.
      </p>
      <Link to="/faq" className="mt-8 inline-block text-sm text-sage underline underline-offset-4">
        Questions? Read the FAQ
      </Link>
    </Reveal>
  </div>
);
